/**
 * The Market screen (docs/market-ux.md): one market at a time with its full cross-venue order
 * book, charts and everything you hold in it.
 *
 *   entry     a destination, not a nav section: the game page's market names and lines link here
 *             (`marketHref`), as do "View market" in Portfolio, Market making and the ticket. No
 *             market in the link → "Pick a market from a game" with a link to Markets.
 *   header    "← <game>" back to the game page · game · market (· ‹ player › on a props market)
 *             (· ‹ line › when the market/player has more than one) · game state (needs
 *             gamestate:read; else the start time) · [Trade] (best venue on the side you pick) ·
 *             [Make market] (Market making's builder on this market and line)
 *   blocks    desktop (≥1024px) two columns: Order book + Venues | Charts + You; mobile stacks
 *             header → book → chart → You → Venues
 *
 * Real mode scopes every venue list to the connected venues, as everywhere else. Nothing here
 * places an order: the ticket does, from its own button.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { LiquidityPosition, PartnerLiquidity } from "@greenbook/client";
import { bestQuote } from "../../lib/marketsBoard.js";
import { bookParticipantId, lineLabel, orderMarkets, quotableMarkets, shortGame, type MmBook, type MmMarket } from "../../lib/marketMaking.js";
import type { MarketLike, PositionLike } from "../../lib/books.js";
import { findBook, marketSides, siblingLines, siblingPlayers, type MarketSide, type MarketTarget } from "../../lib/marketView.js";
import { fmtKickoff, fmtPrice, fmtRelative } from "../../lib/format.js";
import { useOddsFormat } from "../../lib/odds.js";
import { SCOPES } from "../../lib/omauth.js";
import { useIsMobile } from "../../lib/responsive.js";
import { Skeleton, SoftCard, TicketDrawer } from "../kit.js";
import { ErrorState } from "../ui.js";
import { heroState } from "../contest/GameHero.js";
import { btnBase, btnIdle, btnOn, btnPrimary, gameLabel } from "../marketMaking/shared.js";
import { mmQuoteHref } from "../../pages/MarketMaking.js";
import type { MarketSource } from "./source.js";
import { OrderBookBlock } from "./OrderBookBlock.js";
import { ChartsBlock } from "./ChartsBlock.js";
import { YouBlock, VenuesBlock } from "./YouVenues.js";

export interface TicketReq {
  position: LiquidityPosition;
  pl: PartnerLiquidity;
  maxPrice?: number;
}

/** Everything a block needs about the market on screen. */
export interface MarketCtx {
  source: MarketSource;
  contestId: string;
  market: MmMarket;
  book: MmBook;
  sides: MarketSide[];
  /** The live positions for each side (stream-patched prices). */
  live: (LiquidityPosition | undefined)[];
  allowed: ReadonlySet<string> | null;
  /** Side the charts / venues table follow (0 | 1). */
  side: number;
  setSide: (i: number) => void;
  openTicket: (t: TicketReq) => void;
}

export function MarketScreen({
  source,
  target: urlTarget,
  onTarget,
}: {
  source: MarketSource;
  /** The deep link (null: no market picked; the screen points at Markets). */
  target: MarketTarget | null;
  /** The market on screen changed (the route mirrors it into the URL). */
  onTarget?: (t: MarketTarget) => void;
}) {
  const isMobile = useIsMobile();
  const [target, setTarget] = useState<MarketTarget | null>(urlTarget);
  const [ticket, setTicket] = useState<TicketReq | null>(null);

  // A new deep link (e.g. "View market" from the ticket while already here).
  const urlKey = urlTarget ? JSON.stringify(urlTarget) : "";
  useEffect(() => {
    setTarget(urlTarget);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey]);

  /** Show another market or line (the header's ‹ ›, or a hash link settling on its keys). */
  const go = useCallback(
    (t: MarketTarget, opts: { keepTicket?: boolean } = {}) => {
      setTarget((cur) => (cur && cur.contestId === t.contestId && cur.marketKey === t.marketKey && cur.bookKey === t.bookKey ? cur : t));
      if (!opts.keepTicket) setTicket(null);
      onTarget?.(t);
    },
    [onTarget],
  );
  const closeTicket = useCallback(() => setTicket(null), []);
  const openTicket = useCallback((t: TicketReq) => setTicket(t), []);

  return (
    <div className="flex h-full min-h-0">
      <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 p-4">
          {target ? <ActiveMarket key={target.contestId} source={source} target={target} onGo={go} openTicket={openTicket} /> : <PickFromGame />}
        </div>
      </div>
      <TicketDrawer open={!!ticket} onClose={closeTicket} mobile={isMobile} header={<span className="text-[11px] font-semibold uppercase tracking-wider text-dim">Trade ticket</span>}>
        {ticket && (
          <div key={`${ticket.position.position_hash}|${ticket.pl.partner_id}|${ticket.maxPrice ?? ""}`} className="flex min-h-0 flex-1 flex-col">
            {source.renderTicket({ ...ticket, onClose: closeTicket })}
          </div>
        )}
      </TicketDrawer>
    </div>
  );
}

/** No (or an unusable) market in the link: markets are opened from a game page. */
function PickFromGame({ note }: { note?: string }) {
  return (
    <SoftCard className="mx-auto flex w-full max-w-md flex-col items-center gap-3 p-6 text-center">
      <span className="text-[14px] font-medium text-ink">Pick a market from a game</span>
      <span className="text-[13px] text-dim">{note ?? "Open a game and tap a market name or a line (Moneyline, −5.5, Points O/U 24.5) to see its full book here."}</span>
      <Link to="/markets" className={`${btnBase} ${btnPrimary} font-semibold`}>
        Markets
      </Link>
    </SoftCard>
  );
}

/* ============================================================= active market */

function ActiveMarket({
  source,
  target,
  onGo,
  openTicket,
}: {
  source: MarketSource;
  target: MarketTarget;
  onGo: (t: MarketTarget, opts?: { keepTicket?: boolean }) => void;
  openTicket: (t: TicketReq) => void;
}) {
  const contest = source.useContest(target.contestId);
  const gb = source.useGameBook(target.contestId);
  const scope = source.useScope();
  const markets = useMemo(() => {
    if (!gb.positions || !gb.markets || gb.isLoading) return null;
    return orderedMarkets(gb.positions, gb.markets);
  }, [gb.positions, gb.markets, gb.isLoading]);
  const found = useMemo(() => (markets ? findBook(markets, target) : null), [markets, target]);
  const game = contest ? gameLabel(contest) : "";

  // Settle the URL on the market's keys (a hash link, or a market without its line), once per book.
  const foundKey = found ? `${found.market.marketKey}|${found.book.key}` : "";
  useEffect(() => {
    if (found && (target.marketKey !== found.market.marketKey || target.bookKey !== found.book.key)) {
      onGo({ contestId: target.contestId, marketKey: found.market.marketKey, bookKey: found.book.key }, { keepTicket: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foundKey]);

  const sides = useMemo(() => (found ? marketSides(found.book).slice(0, 2) : []), [found]);
  const [side, setSide] = useState(0);
  const bookKey = foundKey;
  useEffect(() => setSide(0), [bookKey]);

  if (gb.isError && !gb.positions) return <ErrorState error={gb.error} onRetry={gb.refetch} />;
  if (!markets) return <MarketSkeleton />;
  // An unknown game (or one with nothing quoted): back to picking one.
  if (markets.length === 0 && !contest) return <PickFromGame note="That link doesn't point at a game with markets. Open a game and tap a market name or a line." />;
  if (!found || sides.length < 2) {
    return (
      <SoftCard className="flex flex-wrap items-center gap-3 p-4 text-[13px] text-dim">
        <span className="min-w-0 flex-1">{game ? `${shortGame(game)}: this` : "This"} market isn't quoted any more.</span>
        <Link to={`/contests/${encodeURIComponent(target.contestId)}`} className={`${btnBase} ${btnIdle}`}>
          Game page
        </Link>
        <Link to="/markets" className={`${btnBase} ${btnIdle}`}>
          Markets
        </Link>
      </SoftCard>
    );
  }

  const ctx: MarketCtx = {
    source,
    contestId: target.contestId,
    market: found.market,
    book: found.book,
    sides,
    live: sides.map((s) => s.position as LiquidityPosition),
    allowed: scope.allowed,
    side,
    setSide,
    openTicket,
  };
  const show = (b: MmBook) => onGo({ contestId: target.contestId, marketKey: found.market.marketKey, bookKey: b.key });

  return (
    <>
      <Header key={`h-${bookKey}`} ctx={ctx} game={game} startsAt={contest?.starts_at ?? null} onBook={show} />
      <div key={bookKey} className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
        <div className="min-w-0 lg:col-start-1 lg:row-start-1">
          <OrderBookBlock ctx={ctx} />
        </div>
        <div className="min-w-0 lg:col-start-2 lg:row-start-1">
          <ChartsBlock ctx={ctx} />
        </div>
        <div className="min-w-0 lg:col-start-2 lg:row-start-2">
          <YouBlock ctx={ctx} />
        </div>
        <div className="min-w-0 lg:col-start-1 lg:row-start-2">
          <VenuesBlock ctx={ctx} />
        </div>
      </div>
    </>
  );
}

const orderedMarkets = (positions: PositionLike[], ms: MarketLike[]): MmMarket[] => orderMarkets(quotableMarkets(positions, ms));

function MarketSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-hidden>
      <Skeleton className="h-12 w-full" />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Skeleton className="h-72 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    </div>
  );
}

/* ===================================================================== header */

function GameState({ source, contestId, startsAt }: { source: MarketSource; contestId: string; startsAt: string | null }) {
  const access = source.useAccess(SCOPES.gamestate);
  const st = heroState(source.useContestState(contestId));
  const start = startsAt ? (Date.parse(startsAt) > Date.now() ? fmtKickoff(startsAt) : `Started ${fmtRelative(startsAt)}`) : null;
  if (!access.ok || !st) return start ? <span className="nums text-dim">{start}</span> : null;
  const score = st.home != null || st.away != null ? `${st.away ?? 0}–${st.home ?? 0}` : null;
  if (st.final) return <span className="nums text-dim">Final{score ? ` · ${score}` : ""}</span>;
  if (st.live)
    return (
      <span className="nums flex items-center gap-1.5 text-blue">
        <span className="pulse inline-block h-1.5 w-1.5 rounded-full bg-blue" aria-hidden />
        {[st.period, st.clock].filter(Boolean).join(" ") || "Live"}
        {score && <span className="text-ink">· {score}</span>}
      </span>
    );
  return start ? <span className="nums text-dim">{start}</span> : null;
}

/** ‹ label ›: walks a list (the market's lines, or its players), each step opening that book. */
function Walk<T>({ items, index, labelOf, onPick, noun }: { items: T[]; index: number; labelOf: (t: T) => string; onPick: (t: T) => void; noun: string }) {
  const btn =
    "grid h-11 w-11 shrink-0 place-items-center rounded-full text-[16px] text-dim hover:bg-raised hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent lg:h-8 lg:w-8";
  return (
    <span className="inline-flex min-w-0 shrink-0 items-center">
      <button type="button" className={btn} onClick={() => onPick(items[index - 1])} disabled={index <= 0} aria-label={`Previous ${noun}`}>
        ‹
      </button>
      <span className="nums min-w-[3.5rem] truncate text-center text-ink" title={`${index + 1} of ${items.length} ${noun}s`}>
        {labelOf(items[index])}
      </span>
      <button type="button" className={btn} onClick={() => onPick(items[index + 1])} disabled={index >= items.length - 1} aria-label={`Next ${noun}`}>
        ›
      </button>
    </span>
  );
}

function Header({ ctx, game, startsAt, onBook }: { ctx: MarketCtx; game: string; startsAt: string | null; onBook: (b: MmBook) => void }) {
  const { format } = useOddsFormat();
  const [trade, setTrade] = useState(false);
  const canMm = ctx.source.useCanMakeMarkets();
  const lines = useMemo(() => siblingLines(ctx.market, ctx.book), [ctx.market, ctx.book]);
  const players = useMemo(() => siblingPlayers(ctx.market), [ctx.market]);
  const pid = bookParticipantId(ctx.book.key);
  const playerIdx = players.findIndex((p) => p.id === (pid ?? ""));
  const lineIdx = lines.findIndex((b) => b.key === ctx.book.key);
  const line = ctx.book.key === "main" ? null : lineLabel(ctx.book);
  const best = ctx.sides.map((s, i) => bestQuote(ctx.live[i] ?? s.position, ctx.allowed));
  const gameHref = `/contests/${encodeURIComponent(ctx.contestId)}`;
  return (
    <SoftCard className="flex flex-col gap-2 p-3">
      <Link to={gameHref} className="-my-1 inline-flex min-h-11 max-w-full items-center self-start truncate text-[12px] text-dim underline-offset-2 hover:text-ink hover:underline lg:min-h-8" title={game}>
        ← {game ? shortGame(game) : "Game"}
      </Link>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1 basis-[16rem]">
          <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-[15px] font-semibold text-ink">
            <span className="min-w-0 truncate text-dim" title={game}>
              {game ? shortGame(game) : "…"}
            </span>
            <span className="text-dim">·</span>
            <span>{ctx.market.label}</span>
            {players.length > 1 && playerIdx >= 0 && (
              <>
                <span className="text-dim">·</span>
                <Walk items={players} index={playerIdx} labelOf={(p) => p.name} onPick={(p) => onBook(p.book)} noun="player" />
              </>
            )}
            {line && (
              <>
                <span className="text-dim">·</span>
                {lines.length > 1 && lineIdx >= 0 ? (
                  <Walk items={lines} index={lineIdx} labelOf={lineLabel} onPick={onBook} noun="line" />
                ) : (
                  <span className="nums">{line}</span>
                )}
              </>
            )}
          </div>
          <div className="mt-0.5 text-[12px]">
            <GameState source={ctx.source} contestId={ctx.contestId} startsAt={startsAt} />
          </div>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <button type="button" aria-expanded={trade} onClick={() => setTrade((v) => !v)} className={`${btnBase} ${trade ? btnOn : btnPrimary} flex-1 font-semibold sm:flex-none`}>
            Trade
          </button>
          {canMm && (
            <Link to={mmQuoteHref({ contestId: ctx.contestId, marketKey: ctx.market.marketKey, bookKey: ctx.book.key })} className={`${btnBase} ${btnIdle} flex-1 sm:flex-none`}>
              Make market
            </Link>
          )}
        </div>
      </div>
      {trade && (
        <div className="gb-fade grid grid-cols-2 gap-2">
          {ctx.sides.map((s, i) => {
            const q = best[i];
            return (
              <button
                key={s.positionHash}
                type="button"
                disabled={!q}
                onClick={() => q && ctx.openTicket({ position: (ctx.live[i] ?? s.position) as LiquidityPosition, pl: q as PartnerLiquidity })}
                className="flex min-h-11 min-w-0 flex-col items-start rounded-lg border border-border-2 bg-panel px-3 py-1.5 text-left hover:border-amber/50 disabled:opacity-40"
              >
                <span className="w-full truncate text-[12px] text-dim">{s.label}</span>
                <span className="flex w-full items-baseline gap-1.5">
                  <span className="nums text-[15px] font-semibold text-ink">{q ? fmtPrice(q.price, q.american_odds, format) : "—"}</span>
                  <span className="min-w-0 truncate text-[11px] text-faint">{q ? (q.partner_name ?? q.partner_id) : "No price"}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </SoftCard>
  );
}

/** A block's card: an uppercase title and controls on the right. */
export function BlockCard({ title, right, children, className = "" }: { title: ReactNode; right?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <SoftCard className={`flex min-w-0 flex-col gap-2 p-3 ${className}`}>
      <div className="flex min-h-8 flex-wrap items-center gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-dim">{title}</h2>
        {right && <div className="ml-auto flex flex-wrap items-center gap-1.5">{right}</div>}
      </div>
      {children}
    </SoftCard>
  );
}

